<template>
  <div class="container">
    <Card class="col mb-8 profile-form-card">
      <template #content>
        <form v-if="viewData.id" id="profileForm" name="profileForm" ref="form">
          <div class="row">
            <div class="col-12">
              <div id="firstName-grp" class="mb-2">
                <label for="firstName" class="fw-bold form-label">{{ t("NAME") }}</label>
                <InputText
                    id="firstName"
                    name="firstName"
                    type="text"
                    :invalid="!firstNameState"
                    required
                    autocomplete="off"
                    class="w-100 text-uppercase"
                    v-model.trim="viewData.firstName"
                />
                <small v-if="!firstNameState" class="text-danger">{{ firstNameFeedback }}</small>
              </div>
            </div>

            <div class="col-12">
              <div id="lastName-grp" class="mb-2">
                <InputText
                    id="lastName"
                    name="lastName"
                    type="text"
                    :invalid="!lastNameState"
                    required
                    autocomplete="off"
                    class="w-100 text-uppercase"
                    v-model.trim="viewData.lastName"
                />
                <small v-if="!lastNameState" class="text-danger">{{ lastNameFeedback }}</small>
              </div>
            </div>

            <div class="col-12">
              <div class="mb-2">
                <div class="fw-bold">{{ t("SSN") }}</div>
                <div>
                  <span>{{ viewData.ssn }}</span>
                  <span
                      v-if="viewData.oldSsn && !auth.hasRole(Role.TRAINEE)"
                      class="ms-2"
                      v-tooltip="t('SSN_CHANGED_INFO_TEXT', {oldSsn: viewData.oldSsn})"
                  >
                    <span class="pi pi-info-circle"/>
                  </span>
                </div>
              </div>

              <div v-if="viewData.role === Role.TRAINEE" class="mb-2">
                <div class="fw-bold">{{ t("CITIZENSHIPS") }}</div>
                <ul class="list-unstyled mb-0" v-if="viewData.citizenships?.length">
                  <li v-for="citizenship of viewData.citizenships" :key="citizenship">
                    <span>{{ t("COUNTRY_CODES." + citizenship) }}</span>
                  </li>
                </ul>
                <span v-else>{{ t("UNKNOWN") }}</span>
              </div>
            </div>
          </div>

          <div id="email-grp" class="mb-2">
            <label for="email" class="fw-bold form-label">{{ t("EMAIL") }}</label>
            <InputText
                id="email"
                type="email"
                :invalid="!emailState"
                required
                autocomplete="off"
                name="email"
                class="w-100"
                v-model.trim="viewData.email"
            />

            <small v-if="!emailState" class="text-danger">{{ emailFeedback }}</small>

            <div v-if="emailUnconfirmed && props.showUnconfirmedEmailAlert && !isShowingConfirmedEmail">
              <p class="mt-2 text-danger small" v-if="confirmedEmail !== viewData.unconfirmedEmail && !route.params.id">
                {{ t("CONFIRMATION_EMAIL_SENT") }}.
              </p>

              <p class="mt-2 text-danger small">
                <span class="me-1" v-if="props.isProfileUpdate">{{ t("PROFILE_UPDATE_EMAIL_UNCONFIRMED") }}.</span>
                <span class="me-1" v-else>{{ t("EMAIL_UNCONFIRMED") }}.</span>

                <template v-if="confirmedEmail && confirmedEmail !== viewData.unconfirmedEmail">
                  <span class="me-1">{{ t("EMAILS_CURRENTLY_SENT_TO_ADDR") }}:</span>
                  <strong>{{ confirmedEmail }}</strong>
                </template>
              </p>

              <Button severity="danger" :label="t('SEND_NEW_CONFIRMATION_EMAIL')" @click="resetEmailToken" v-if="!route.params.id"/>
            </div>
          </div>

          <div class="row">
            <div class="col-xl-6">
              <div id="dialCode-grp" class="mb-2">
                <label for="dialCode" class="fw-bold form-label">{{ t("DIAL_CODE") }}</label>
                <Select
                    label-id="dialCode"
                    name="dialCode"
                    :options="areaCodes"
                    option-label="text"
                    option-value="value"
                    v-model="viewData.dialCode"
                    :placeholder="t('SELECT_DIAL')"
                    required
                    class="w-100"
                    :invalid="!dialCodeState"
                />
              </div>
            </div>

            <div class="col-xl-6">
              <div id="phone-grp" class="mb-2">
                <label for="phone" class="fw-bold form-label">{{ t("PHONE") }}</label>
                <InputText
                    id="phone"
                    name="phone"
                    autocomplete="off"
                    placeholder="1234567"
                    :invalid="!phoneState"
                    required
                    type="text"
                    class="w-100"
                    v-model.trim="viewData.phone"
                    @input="onPhoneInput"
                    :maxlength="getMaxPhoneLength(viewData.dialCode)"
                />
                <small v-if="!phoneState" class="text-danger">{{ phoneFeedback }}</small>
              </div>
            </div>
          </div>

          <div id="address-grp" class="mb-2">
            <label for="address" class="fw-bold form-label">{{ t("ADDRESS") }}</label>
            <Textarea
                class="textarea w-100"
                id="address"
                name="address"
                autocomplete="off"
                rows="4"
                v-model.trim="viewData.address"
                :invalid="!addressState"
            />
            <small v-if="!addressState" class="text-danger">{{ addressFeedback }}</small>
          </div>

          <div id="education-grp" class="mb-2">
            <label for="education" class="fw-bold form-label">{{ t("EDUCATION") }}</label>
            <Select
                :options="educationOptions"
                label-id="education"
                name="education"
                v-model="viewData.education"
                option-label="text"
                option-value="value"
                :placeholder="t('SELECT_OPTION')"
                required
                class="w-100"
                :invalid="!educationState"
            />
            <small v-if="!educationState" class="text-danger">{{ educationFeedback }}</small>
          </div>

          <div id="details-grp" class="mb-2">
            <label for="details" class="fw-bold form-label">{{ t("DETAILS") }}</label>
            <small class="d-block text-muted">{{ t('DETAILS_NOTE') }}</small>
            <Textarea
                class="textarea w-100"
                id="details"
                name="details"
                rows="2"
                :placeholder="t('WRITE_STH')"
                v-model.trim="viewData.details"
            />
          </div>

          <div id="origin-grp" class="mb-2">
            <label for="origin" class="fw-bold form-label">{{ t("LETUSKNOW") }}</label>
            <Select
                :options="originOptions"
                label-id="origin"
                name="origin"
                v-model="viewData.origin"
                option-label="text"
                option-value="value"
                :placeholder="t('SELECT_OPTION')"
                required
                class="w-100"
                :invalid="!originState"
            />
            <small v-if="!originState" class="text-danger">{{ originFeedback }}</small>
          </div>

          <div class="mb-2" v-if="auth.hasRole(Role.ADMIN) && auth.getAcc() !== viewData.id">
            <label for="active-till" class="form-label">{{ t('ACTIVE_TILL') }}</label>
            <DatePicker
                v-model="viewData.activeTill"
                input-id="active-till"
                :placeholder="t('SELECT_DATE')"
                date-format="dd.mm.yy"
                class="w-100"
            />
          </div>

          <div class="mb-4" v-if="showCourseRegistrationNotificationsToggle">
            <div class="fw-bold mb-2">{{ t("COURSE_REG_NOTIF_ENABLED") }}</div>
            <div class="d-flex align-items-center mb-2">
              <ToggleSwitch
                  v-model="viewData.courseRegistrationNotificationsEnabled"
                  input-id="course-registration-notifications"
              />
              <label class="ms-2 mb-0" for="course-registration-notifications">
                {{ viewData.courseRegistrationNotificationsEnabled ? t("YES") : t("NO") }}
              </label>
            </div>
            <small class="text-muted">{{ t("COURSE_REG_NOTIF_ENABLED_HELP") }}</small>
          </div>

          <Button
              class="w-100"
              v-show="route.path !== '/groups'"
              id="profileSubmit"
              :label="t('SAVE')"
              @click="onSubmit"
          />
        </form>
      </template>
    </Card>
  </div>
</template>

<script setup>
import codes from "@/area_codes";
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {injectAxios, injectAuth} from "@/util/inject";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {getMaxPhoneLength, validatePhone} from "@/util/phoneUtil";
import {Role} from "@/auth/oauth";
import {useToast} from "@/util/toastUtils";
import {validatePersonName, isValidEmail, isValidAddressFormat} from "@/util/validationUtils";
import {getEducationOptions} from "@/model/education.ts";
import {useDateFormat} from "@/mixins/dateFormatMixin2.js";

const axios = injectAxios();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const {t, locale} = useI18n();
const auth = injectAuth();
const {dateFormatIso} = useDateFormat();

const viewData = ref({
  firstName: "",
  lastName: "",
  ssn: "",
  email: "",
  unconfirmedEmail: "",
  phone: "",
  dialCode: "",
  address: "",
  education: "",
  details: "",
  origin: "",
  role: "",
  isArchived: false,
  courseRegistrationNotificationsEnabled: true,
});

const emit = defineEmits(['update:validity', 'afterSave']);

const emailUnconfirmed = ref(false);
const confirmedEmail = ref("");
const confirmedEmailCheckInterval = ref(0);

const areaCodes = ref(
    codes.map((code) => ({
      value: code.dial_code,
      text: `${code.name} (${code.dial_code})`,
    }))
);

const educationOptions = computed(() => getEducationOptions(t));

const originOptions = computed(() => [
  {value: "UNSPECIFIED", text: t("NOT_SPECIFIED")},
  {value: "PPA", text: t("PPA")},
  {value: "SOCIAL_MEDIA", text: t("SOCIAL_MEDIA")},
  {value: "FRIENDS", text: t("FRIENDS")},
  {value: "WORK", text: t("WORK")},
  {value: "SCHOOL", text: t("SCHOOL")},
  {value: "LOCAL_MUNICIPALITY", text: t("LOCAL_MUNICIPALITY")},
  {value: "OTHER", text: t("OTHER")},
]);

const props = defineProps({
  createNewTokenOnUpdate: Boolean,
  showUnconfirmedEmailAlert: Boolean,
  isProfileUpdate: Boolean,
});

function onPhoneInput(e) {
  viewData.value.phone = e.target.value.replaceAll(/\D/g, "");
}

const requireAllFormData = computed(() => {
  return !route.params.id || !auth.hasSomeRole(Role.ADMIN, Role.SCHOOL_MANAGER, Role.VIEWER);
});

const requireExtraFormData = computed(() => {
  return viewData.value.role === Role.TRAINEE && requireAllFormData.value;
});

const showCourseRegistrationNotificationsToggle = computed(() => {
  return viewData.value.role === Role.TRAINEE && !route.params.id;
});

const firstNameState = computed(() => {
  return validatePersonName(viewData.value.firstName);
});

const firstNameFeedback = computed(() => {
  return firstNameState.value ? "" : t("NAME_AGAINST_POLICY");
});

const lastNameState = computed(() => {
  return validatePersonName(viewData.value.lastName);
});

const lastNameFeedback = computed(() => {
  return lastNameState.value ? "" : t("NAME_AGAINST_POLICY");
});

const emailState = computed(() => {
  return (!requireAllFormData.value && !viewData.value.email) || isValidEmail(viewData.value.email);
});

const emailFeedback = computed(() => {
  return emailState.value ? "" : t("FAULTY_EMAIL");
});

const isShowingConfirmedEmail = computed(() =>
  confirmedEmail.value &&
  viewData.value.email &&
  viewData.value.email.toLowerCase() === confirmedEmail.value.toLowerCase()
);

const phoneState = computed(() => {
  return (!requireAllFormData.value && !viewData.value.phone) || validatePhone(viewData.value?.dialCode, viewData.value?.phone);
});

const phoneFeedback = computed(() => {
  return phoneState.value ? "" : t("FAULTY_PHONE", {maxLength: getMaxPhoneLength(viewData.value?.dialCode)});
});

const dialCodeState = computed(() => {
  return !requireAllFormData.value || !!viewData.value?.dialCode;
});

const addressState = computed(() => {
  return !requireExtraFormData.value || !viewData.value.address || isValidAddressFormat(viewData.value.address.trim());
});

const addressFeedback = computed(() => {
  return addressState.value ? "" : t("TEXT_RESTRICTION");
});

const educationState = computed(() => {
  return !requireExtraFormData.value || (!!viewData.value.education && viewData.value.education !== educationOptions.value[0].value);
});

const educationFeedback = computed(() => {
  return educationState.value ? "" : t("ERRORS.FIELD_IS_REQUIRED");
});

const originState = computed(() => {
  return !requireExtraFormData.value || (!!viewData.value.origin && viewData.value.origin !== originOptions.value[0].value);
});

const originFeedback = computed(() => {
  return originState.value ? "" : t("ERRORS.FIELD_IS_REQUIRED");
});

const formIsValid = computed(() => {
  return (
      firstNameState.value &&
      lastNameState.value &&
      emailState.value &&
      phoneState.value &&
      dialCodeState.value &&
      addressState.value &&
      educationState.value &&
      originState.value
  );
});

function onSubmit() {
  if (formIsValid.value) {
    updateAccount();
  } else {
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
  }
}

async function accountData() {
  let response;

  if (route.params.id) {
    response = await axios.get(`/account/${route.params.id}`);
  } else {
    response = await axios.get("/account");
  }

  if (response.data && response) {
    viewData.value = {
      ...response.data,
      education: response.data.education || educationOptions.value[0].value,
      origin: response.data.origin || originOptions.value[0].value,
      email: response.data.unconfirmedEmail || response.data.email,
      activeTill: response.data.activeTill ? new Date(response.data.activeTill) : null,
      courseRegistrationNotificationsEnabled: response.data.courseRegistrationNotificationsEnabled !== false,
    };

    emailUnconfirmed.value = response.data.unconfirmedEmail?.length > 0;
    confirmedEmail.value = response.data.email;
  }

  if (viewData.value.isArchived && !!route.params.id && !auth.canSeeArchivedAccounts()) {
    await router.push({name: "Groups"});
  }

  if (emailUnconfirmed.value && props.showUnconfirmedEmailAlert && !route.params.id) {
    pollForConfirmedEmail();
  }
}

async function updateAccount() {
  const requestData = {
    ...viewData.value,
    createNewToken: !!props.createNewTokenOnUpdate,
    activeTill: viewData.value.activeTill ? dateFormatIso(viewData.value.activeTill) : null,
  };

  const response = await axios.put(`/account/${viewData.value.id}/`, requestData);

  if (showCourseRegistrationNotificationsToggle.value) {
    await axios.patch("/account/course-registration-notifications", {
      enabled: viewData.value.courseRegistrationNotificationsEnabled,
    });
  }

  await accountData();

  if (props.createNewTokenOnUpdate && response.data?.jwtToken) {
    auth.clearContext();
    await auth.login(response.data.jwtToken);
    return;
  }

  toast.makeSuccessToast(t("SAVED"));
  emit("afterSave");

  if (response.data.emailSent) {
    toast.makeSuccessToast(t("CONFIRMATION_EMAIL_SENT"));
  }
}

const resetEmailToken = async () => {
  const submittedEmail = viewData.value.email.trim();

  if (!isValidEmail(submittedEmail)) {
    toast.makeErrorToast(t("FAULTY_EMAIL"));
    return;
  }

  if (isShowingConfirmedEmail.value) {
    await updateAccount();
    return;
  }

  await axios.put("/account/reset-email-token", { email: submittedEmail });
  emailUnconfirmed.value = true;
  viewData.value.unconfirmedEmail = submittedEmail;
  viewData.value.email = submittedEmail;
  toast.makeSuccessToast(t("CONFIRMATION_EMAIL_SENT"));
};

const pollForConfirmedEmail = () => {
  clearInterval(confirmedEmailCheckInterval.value);

  confirmedEmailCheckInterval.value = setInterval(async () => {
    const resp = await axios.get("/account/email-confirmed");
    emailUnconfirmed.value = !resp.data.confirmed;

    if (resp.data.confirmed) {
      clearInterval(confirmedEmailCheckInterval.value);
    }
  }, 5000);
};

onMounted(async () => {
  await accountData();
});

onBeforeUnmount(() => {
  clearInterval(confirmedEmailCheckInterval.value);
});

watch(formIsValid, (newVal) => {
  emit('update:validity', newVal);
});

defineExpose({
  getFormData: () => viewData.value,
});
</script>

<style scoped>
.profile-form-card :deep(.p-card-body) {
  text-align: left;
}
</style>
